import { isServiceFormFieldType, isServiceFormGridColumnType, isServiceFormKind, isServiceFormLayoutGrid, SERVICE_FORM_BINDING_KEYS } from '../types/serviceForms.js';
import { resolveServiceFormFieldValueFromContext } from './serviceFormBindings.js';
export function collectServiceFormValueFields(defs) {
    const out = [];
    const visit = (d) => {
        if (d.type === 'paragraph')
            return;
        if (isServiceFormLayoutGrid(d)) {
            for (const cell of d.cells || []) {
                if (cell)
                    visit(cell);
            }
            return;
        }
        out.push(d);
    };
    for (const d of defs)
        visit(d);
    return out;
}
/** Document order including paragraphs; unwraps layout grids. Used on mobile where cells stack. */
export function flattenServiceFormFillFields(defs) {
    const out = [];
    const visit = (d) => {
        if (isServiceFormLayoutGrid(d)) {
            for (const cell of d.cells || []) {
                if (cell)
                    visit(cell);
            }
            return;
        }
        out.push(d);
    };
    for (const d of defs)
        visit(d);
    return out;
}
export function emptyServiceFormLayoutCells(columnCount, rowCount) {
    return Array.from({ length: columnCount * rowCount }, () => null);
}
export function resizeServiceFormLayoutCells(cells, oldColumnCount, oldRowCount, newColumnCount, newRowCount) {
    const next = emptyServiceFormLayoutCells(newColumnCount, newRowCount);
    const src = cells || [];
    for (let r = 0; r < Math.min(oldRowCount, newRowCount); r++) {
        for (let c = 0; c < Math.min(oldColumnCount, newColumnCount); c++) {
            next[r * newColumnCount + c] = src[r * oldColumnCount + c] ?? null;
        }
    }
    return next;
}
/** Merges submitted field values with binding-only values for fields where `promptAtCompletion` is false. */
export function mergeServiceFormCompletionValues(defs, submitted, bindingContext) {
    const finalValuesByFieldId = { ...submitted };
    for (const d of collectServiceFormValueFields(defs)) {
        if (d.promptAtCompletion === false) {
            finalValuesByFieldId[d.id] = resolveServiceFormFieldValueFromContext(d, bindingContext);
        }
    }
    return finalValuesByFieldId;
}
function isEmptyValue(v) {
    return v === undefined || v === null || v === '';
}
export function assertServiceFormRequiredFieldsFilled(defs, finalValuesByFieldId) {
    for (const d of collectServiceFormValueFields(defs)) {
        if (!d.required)
            continue;
        const v = finalValuesByFieldId[d.id];
        if (d.type === 'checkbox') {
            if (v === undefined || v === null) {
                throw new Error(`Field "${d.label}" is required`);
            }
        }
        else if (d.type === 'grid') {
            const rows = normalizeServiceFormGridValue(d, v);
            const filled = rows.some((row) => !isServiceFormGridRowEmpty(d.columns || [], row));
            if (!filled) {
                throw new Error(`Field "${d.label}" is required`);
            }
        }
        else if (isEmptyValue(v)) {
            throw new Error(`Field "${d.label}" is required`);
        }
    }
}
export function parseServiceFormFieldsJson(raw) {
    if (!Array.isArray(raw))
        return [];
    return raw.filter((item) => {
        if (!item || typeof item !== 'object')
            return false;
        const f = item;
        return typeof f.id === 'string' && typeof f.label === 'string' && typeof f.type === 'string';
    });
}
const FIELD_TYPE_ALIASES = {
    text: 'text',
    string: 'text',
    input: 'text',
    short_text: 'text',
    textarea: 'textarea',
    multiline: 'textarea',
    notes: 'textarea',
    comment: 'textarea',
    comments: 'textarea',
    number: 'number',
    numeric: 'number',
    integer: 'number',
    amount: 'number',
    date: 'date',
    datetime: 'date',
    checkbox: 'checkbox',
    bool: 'checkbox',
    boolean: 'checkbox',
    yes_no: 'checkbox',
    yesno: 'checkbox',
    dropdown: 'dropdown',
    select: 'dropdown',
    choice: 'dropdown',
    radio: 'dropdown',
    signature: 'signature',
    sign: 'signature',
    initials: 'signature',
    grid: 'grid',
    table: 'grid',
    matrix: 'grid',
    layout: 'grid',
    layout_grid: 'grid',
    paragraph: 'paragraph',
    heading: 'paragraph',
    instructions: 'paragraph',
    note: 'paragraph',
    static: 'paragraph',
    static_text: 'paragraph',
    printed: 'paragraph',
    printed_text: 'paragraph'
};
const KIND_ALIASES = {
    certificate: 'certificate',
    cert: 'certificate',
    checklist: 'checklist',
    check: 'checklist',
    check_list: 'checklist',
    maintenance_log: 'maintenance_log',
    maintenance: 'maintenance_log',
    log: 'maintenance_log',
    custom: 'custom',
    form: 'custom'
};
const BINDING_SET = new Set(SERVICE_FORM_BINDING_KEYS.filter(Boolean));
function slugifyFieldId(raw, fallback) {
    const s = raw
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '')
        .slice(0, 48);
    return s || fallback;
}
function coerceFieldType(raw) {
    if (typeof raw !== 'string')
        return 'text';
    const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
    return FIELD_TYPE_ALIASES[key] || 'text';
}
function coerceKind(raw) {
    if (isServiceFormKind(raw))
        return raw;
    if (typeof raw !== 'string')
        return 'custom';
    const key = raw.trim().toLowerCase().replace(/[\s-]+/g, '_');
    return KIND_ALIASES[key] || 'custom';
}
function mapAiFieldItem(f, i, used, allowGrid) {
    const type = coerceFieldType(f.type);
    if (type === 'grid' && !allowGrid)
        return null;
    const label = typeof f.label === 'string' ? f.label.trim() : '';
    if (!label && type !== 'paragraph' && type !== 'grid')
        return null;
    let id = typeof f.id === 'string' && f.id.trim()
        ? slugifyFieldId(f.id.trim(), `field_${i + 1}`)
        : slugifyFieldId(label || (type === 'grid' ? 'section' : 'paragraph'), `field_${i + 1}`);
    if (used.has(id))
        id = `${id}_${i + 1}`;
    used.add(id);
    const options = Array.isArray(f.options)
        ? f.options.filter((o) => typeof o === 'string').map((o) => o.trim()).filter(Boolean)
        : undefined;
    const binding = typeof f.defaultBinding === 'string' ? f.defaultBinding.trim() : '';
    const hasBinding = BINDING_SET.has(binding);
    const mapped = {
        id,
        label: (label || (type === 'grid' ? 'Section' : 'Static text')).slice(0, 120),
        type,
        required: Boolean(f.required),
        options: type === 'dropdown' ? (options && options.length > 0 ? options : ['Yes', 'No', 'N/A']) : options,
        defaultBinding: type === 'grid' || type === 'paragraph' || !hasBinding ? undefined : binding,
        promptAtCompletion: type === 'paragraph'
            ? false
            : type === 'grid'
                ? true
                : f.promptAtCompletion === false && hasBinding
                    ? false
                    : true,
        content: type === 'paragraph' && typeof f.content === 'string' ? f.content : undefined
    };
    if (type !== 'grid')
        return mapped;
    const layout = flattenAiLayoutCells(f.cells);
    if (layout) {
        const requestedCols = Number(f.columnCount);
        const requestedRows = Number(f.rowCount);
        const columnCount = Number.isFinite(requestedCols) && requestedCols >= 1 ? requestedCols : layout.columnCount;
        const rowCount = Number.isFinite(requestedRows) && requestedRows >= 1
            ? requestedRows
            : Math.max(layout.rowCount, Math.ceil(layout.cells.length / columnCount));
        const size = Math.min(4, Math.max(1, Math.round(columnCount))) * Math.min(12, Math.max(1, Math.round(rowCount)));
        const cells = [...layout.cells];
        while (cells.length < size)
            cells.push(null);
        mapped.columnCount = columnCount;
        mapped.rowCount = rowCount;
        mapped.cells = cells.slice(0, size).map((cell, c) => {
            if (!cell || typeof cell !== 'object' || Array.isArray(cell))
                return null;
            return mapAiFieldItem(cell, c, used, false);
        });
        return mapped;
    }
    mapped.columns = f.columns;
    mapped.rowCount = f.rowCount;
    return mapped;
}
/** Accepts a flat cell list or a 2D row array from the model. */
export function flattenAiLayoutCells(raw) {
    if (!Array.isArray(raw) || raw.length === 0)
        return null;
    const looksNested = raw.some((row) => Array.isArray(row));
    if (looksNested) {
        const rows = raw.map((row) => (Array.isArray(row) ? row : [row]));
        const columnCount = Math.min(4, Math.max(1, ...rows.map((row) => row.length)));
        const rowCount = Math.min(12, Math.max(1, rows.length));
        const cells = [];
        for (let r = 0; r < rowCount; r++) {
            const row = rows[r] || [];
            for (let c = 0; c < columnCount; c++)
                cells.push(row[c] ?? null);
        }
        return { cells, columnCount, rowCount };
    }
    const n = raw.length;
    let columnCount;
    let rowCount;
    if (n === 4) {
        columnCount = 2;
        rowCount = 2;
    }
    else if (n === 6) {
        columnCount = 3;
        rowCount = 2;
    }
    else if (n <= 4) {
        columnCount = n;
        rowCount = 1;
    }
    else {
        columnCount = 2;
        rowCount = Math.ceil(n / 2);
    }
    columnCount = Math.min(4, Math.max(1, columnCount));
    rowCount = Math.min(12, Math.max(1, rowCount));
    const size = columnCount * rowCount;
    const cells = raw.slice(0, size);
    while (cells.length < size)
        cells.push(null);
    return { cells, columnCount, rowCount };
}
/**
 * Maps a model JSON payload into a valid service-form draft.
 * Invalid types/bindings are coerced; empty dropdowns get Yes/No/N/A.
 */
export function normalizeServiceFormDraftFromAi(raw) {
    const record = raw && typeof raw === 'object' ? raw : {};
    const name = typeof record.name === 'string' && record.name.trim()
        ? record.name.trim().slice(0, 120)
        : 'Imported form';
    const description = typeof record.description === 'string' && record.description.trim()
        ? record.description.trim().slice(0, 500)
        : null;
    const kind = coerceKind(record.kind);
    const rawFields = Array.isArray(record.fields) ? record.fields : [];
    const used = new Set();
    const mapped = [];
    for (let i = 0; i < rawFields.length && mapped.length < 80; i++) {
        const item = rawFields[i];
        if (!item || typeof item !== 'object')
            continue;
        const next = mapAiFieldItem(item, i, used, true);
        if (next)
            mapped.push(next);
    }
    const fields = normalizeServiceFormFields(mapped);
    if (fields.length === 0) {
        throw new Error('No fillable fields were found on this PDF');
    }
    return { name, description, kind, fields };
}
const DEFAULT_GRID_COLUMNS = [
    { id: 'col_item', label: 'Item', type: 'text' },
    { id: 'col_qty', label: 'Qty', type: 'number' },
    { id: 'col_notes', label: 'Notes', type: 'text' }
];
export function normalizeServiceFormGridColumns(raw) {
    if (!Array.isArray(raw))
        return [...DEFAULT_GRID_COLUMNS];
    const used = new Set();
    const cols = [];
    for (let i = 0; i < raw.length && cols.length < 8; i++) {
        const item = raw[i];
        if (!item || typeof item !== 'object')
            continue;
        const c = item;
        const label = typeof c.label === 'string' ? c.label.trim() : '';
        if (!label)
            continue;
        let id = typeof c.id === 'string' && c.id.trim() ? c.id.trim() : `col_${i + 1}`;
        if (used.has(id))
            id = `${id}_${i + 1}`;
        used.add(id);
        const type = isServiceFormGridColumnType(c.type) ? c.type : 'text';
        const options = Array.isArray(c.options)
            ? c.options.filter((o) => typeof o === 'string').map((o) => o.trim()).filter(Boolean)
            : undefined;
        cols.push({
            id,
            label: label.slice(0, 40),
            type,
            options: type === 'dropdown' ? (options && options.length > 0 ? options : ['Yes', 'No', 'N/A']) : undefined
        });
    }
    return cols.length > 0 ? cols : [...DEFAULT_GRID_COLUMNS];
}
export function clampServiceFormGridRowCount(raw) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n))
        return 4;
    return Math.min(20, Math.max(1, Math.round(n)));
}
export function isServiceFormGridRowEmpty(columns, row) {
    return columns.every((col) => {
        const v = row[col.id];
        if (col.type === 'checkbox')
            return !v;
        return v === undefined || v === null || v === '';
    });
}
export function normalizeServiceFormGridValue(def, raw) {
    const columns = def.columns || [];
    const min = clampServiceFormGridRowCount(def.rowCount);
    const source = Array.isArray(raw) ? raw : [];
    const rows = [];
    for (const item of source) {
        if (!item || typeof item !== 'object' || Array.isArray(item))
            continue;
        const rec = item;
        const row = {};
        for (const col of columns) {
            row[col.id] = rec[col.id];
        }
        rows.push(row);
        if (rows.length >= 40)
            break;
    }
    while (rows.length < min)
        rows.push({});
    return rows;
}
export function clampServiceFormColumnCount(raw) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n))
        return 2;
    return Math.min(4, Math.max(1, Math.round(n)));
}
export function clampServiceFormLayoutRowCount(raw) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    if (!Number.isFinite(n))
        return 2;
    return Math.min(12, Math.max(1, Math.round(n)));
}
function normalizeOneField(item, i, used) {
    if (!item || typeof item !== 'object') {
        throw new Error(`Invalid field at index ${i}`);
    }
    const f = item;
    let id = typeof f.id === 'string' && f.id.trim() ? f.id.trim() : `field_${i + 1}`;
    if (used.has(id)) {
        id = `${id}_${i + 1}`;
    }
    if (used.has(id)) {
        throw new Error(`Duplicate field id "${id}"`);
    }
    used.add(id);
    const typeRaw = f.type;
    if (!isServiceFormFieldType(typeRaw)) {
        throw new Error(`Field ${i}: invalid type`);
    }
    const isParagraph = typeRaw === 'paragraph';
    const isGrid = typeRaw === 'grid';
    let label = typeof f.label === 'string' ? f.label.trim() : '';
    if (!label) {
        if (isParagraph)
            label = 'Static text';
        else
            throw new Error(`Field ${i}: label is required`);
    }
    const options = Array.isArray(f.options)
        ? f.options.filter((o) => typeof o === 'string').map((o) => o.trim()).filter(Boolean)
        : undefined;
    if (typeRaw === 'dropdown' && (!options || options.length === 0)) {
        throw new Error(`Field "${label}": dropdown options are required`);
    }
    const content = isParagraph && typeof f.content === 'string' ? f.content.trim().slice(0, 4000) : undefined;
    let columns;
    let cells;
    let columnCount;
    let rowCount;
    if (isGrid && Array.isArray(f.cells)) {
        columnCount = clampServiceFormColumnCount(f.columnCount);
        rowCount = clampServiceFormLayoutRowCount(f.rowCount);
        const size = columnCount * rowCount;
        const source = f.cells;
        cells = [];
        for (let c = 0; c < size; c++) {
            const rawCell = source[c];
            if (!rawCell || typeof rawCell !== 'object') {
                cells.push(null);
                continue;
            }
            const cellRec = rawCell;
            if (cellRec.type === 'grid') {
                cells.push(null);
                continue;
            }
            try {
                cells.push(normalizeOneField(rawCell, c, used));
            }
            catch {
                cells.push(null);
            }
        }
    }
    else if (isGrid) {
        columns = normalizeServiceFormGridColumns(f.columns);
        rowCount = clampServiceFormGridRowCount(f.rowCount);
    }
    return {
        id,
        label,
        type: typeRaw,
        required: isParagraph ? false : Boolean(f.required),
        options: isGrid || isParagraph ? undefined : options,
        defaultBinding: isGrid || isParagraph ? undefined : typeof f.defaultBinding === 'string' ? f.defaultBinding : undefined,
        promptAtCompletion: isParagraph ? false : isGrid ? true : f.promptAtCompletion === false ? false : true,
        columns,
        rowCount,
        columnCount,
        cells,
        content
    };
}
export function normalizeServiceFormFields(raw) {
    if (!Array.isArray(raw)) {
        throw new Error('fields must be an array');
    }
    const used = new Set();
    return raw.map((item, i) => normalizeOneField(item, i, used));
}
export function serviceFormInstanceMatchesEmailScope(instance, scope) {
    if (instance.deletedAt)
        return false;
    if (instance.status !== 'completed' || !instance.generatedFileId)
        return false;
    if (scope.jobId && instance.jobId === scope.jobId)
        return true;
    if (scope.invoiceId && instance.invoiceId === scope.invoiceId)
        return true;
    return false;
}
//# sourceMappingURL=serviceFormValues.js.map